import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import CatalogService from '../../services/CatalogService';
import FavoriteService from '../../services/FavoriteService';
import { extractError } from '../../helpers/api';
import { getToken } from '../../helpers/handleToken';

const model = {
    catalogCategories: [],
    catalogUrl: [],
    productList: [],
    productLoading: false,
    productListSearch: [],
    productDetails: null,
    loading: false,
    error: null,
    productListState: '',
    favoriteList: [],
    message: '',
}

export const getCategories = createAsyncThunk(
    'catalog/getCategories',
    async (_, thunkApi) => {
        try {
            const response = await CatalogService.getCategories();
            const { catalog: catalogCategories, url: urlCategories } = response.data;
            return { catalogCategories, urlCategories };
        } catch (error) {
            return thunkApi.rejectWithValue(extractError(error));
        }
    },
);

export const getProductList = createAsyncThunk(
    'catalog/getProductList',
    async (data, thunkApi) => {
        let response;
        try {
            const token = getToken();
            if (token) {
                response = await CatalogService.getProductListAsAuthUser(data);
            } else {
                response = await CatalogService.getProductList(data);
            }
            return response.data;
        } catch (error) {
            return thunkApi.rejectWithValue(extractError(error));
        }
    },
);

export const getProductListSearch = createAsyncThunk(
    'catalog/getProductListSearch',
    async (data, thunkApi) => {
        try {
            const response = await CatalogService.getProductListSearch(data);
            return response.data;
        } catch (error) {
            return thunkApi.rejectWithValue(extractError(error));
        }
    },
);

export const viewProduct = createAsyncThunk(
    'catalog/viewProduct',
    async (data, thunkApi) => {
        let response;
        try {
            const token = getToken();
            if (token) {
                response = await CatalogService.viewProductAsAuthUser(data);
            } else {
                response = await CatalogService.viewProduct(data);
            }
            return response.data;
        } catch (error) {
            return thunkApi.rejectWithValue(extractError(error));
        }
    },
);

export const addOrRemoveFavorite = createAsyncThunk(
    'catalog/addOrRemoveFavorite',
    async (data, thunkApi) => {
        try {
            const response = await FavoriteService.addOrRemoveFavorite(data);
            return {
                requestData: data.favorites,
                responseData: response.data,
            };
        }
        catch (error) {
            return thunkApi.rejectWithValue(extractError(error));
        }
    },
);

const returnFavorites = (data) => {
    return data.flatMap(item =>
        item.categories.flatMap(category => {
            if (category.categories !== null) {
                return category.categories.flatMap(subcategory =>
                    subcategory.products
                        .filter(product => product.is_favorite)
                        .map(product => ({
                            product_id: product.id,
                            category_id: subcategory.id,
                        }))
                );
            } else {
                return category.products
                    .filter(product => product.is_favorite)
                    .map(product => ({
                        product_id: product.id,
                        category_id: category.id,
                    }));
            }
        })
    );
};

const catalogSlice = createSlice({
    name: 'catalog',
    initialState: model,
    reducers: {
        clearProductListSearch: (state) => {
            state.productListSearch = [];
        },
    },
    extraReducers: (builder) => {
        // get category list
        builder.addCase(getCategories.pending, (state) => {
            state.loading = true;
        }).addCase(getCategories.fulfilled, (state, action) => {
            state.loading = false;
            state.catalogCategories = action.payload.catalogCategories;
            state.catalogUrl = action.payload.urlCategories;
        }).addCase(getCategories.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // get product list
        builder.addCase(getProductList.pending, (state) => {
            state.productLoading = true;
        }).addCase(getProductList.fulfilled, (state, action) => {
            state.productList = action.payload;
            state.productListState = action.payload.length > 0 ? 'Has data' : 'No data';
            state.productLoading = false;
            state.favoriteList = returnFavorites(action.payload);
        }).addCase(getProductList.rejected, (state, action) => {
            state.error = action.payload;
            state.productLoading = false;
        });

        // get product list (search component)
        builder.addCase(getProductListSearch.pending, (state) => {
            state.loading = true;
        }).addCase(getProductListSearch.fulfilled, (state, action) => {
            state.productListSearch = action.payload;
            state.loading = false;
        }).addCase(getProductListSearch.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = false;
        });

        // view product
        builder.addCase(viewProduct.pending, (state) => {
            state.loading = true;
        }).addCase(viewProduct.fulfilled, (state, action) => {
            state.productDetails = action.payload;
            state.loading = false;
        }).addCase(viewProduct.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = false;
        });

        // add or remove from favorites
        builder.addCase(addOrRemoveFavorite.pending, (state) => {
            state.loading = true;
        }).addCase(addOrRemoveFavorite.fulfilled, (state, action) => {
            state.loading = false;
            state.favoriteList = action.payload.responseData.length < 1
                ? state.favoriteList.filter((item) => !action.payload.requestData.some((el) => el.product_id === item.product_id && el.category_id === item.category_id))
                : [...state.favoriteList, ...action.payload.responseData];
            state.message = action.payload.responseData.length < 1 ? 'Removed from favorites' : 'Added to favorites';
        }).addCase(addOrRemoveFavorite.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
    },
});

export const { clearProductListSearch } = catalogSlice.actions;

export default catalogSlice.reducer;
